import { ref, computed, InjectionKey, onMounted, onBeforeUnmount } from 'vue'
import { api } from '@/plugins'
import { buildQuery } from '@/utils/query'

// Load items
export type UseItem = {
  loading: boolean
  items: any
  getItems: (query: Record<string, any>) => Promise<void>
}

export const ItemSymbol: InjectionKey<UseItem> = Symbol('ItemSymbol')
export const useGetItems = (url: string) => {
  const loading = ref(false)
  const items = ref([])

  const getItems = async (query: Record<string, any>) => {
    loading.value = true
    return api
      .get(`${url}?${buildQuery(query)}`)
      .then((res) => {
        items.value = res.data
      })
      .catch(() => {
        items.value = []
        Promise.resolve()
      })
      .finally(() => (loading.value = false))
  }

  return {
    loading,
    items,
    getItems
  }
}

export type UsePagination = {
  currentPage: number
  pageSize: number
  totalItem: number
  totalPage: number
  loading: boolean
  items: any
}

export const PaginationSymbol: InjectionKey<UsePagination> =
  Symbol('PaginationSymbol')
export const usePagination = (url: string, pSize = 15) => {
  const currentPage = ref(1)
  const pageSize = ref(pSize)
  const totalItem = ref(0)
  const items = ref([])
  const loading = ref(false)

  const totalPage = computed(() => {
    return Math.ceil(totalItem.value / pageSize.value)
  })

  const getItems = (query: Record<string, any> = { is_get_sorted: false }, infinityLoad = false, customUrl = null) => {
    if(!infinityLoad) loading.value = true
    let _url = url
    if(customUrl) _url = customUrl
    if (query.page === undefined && query.is_get_sorted !== true) {
      query.page = currentPage.value
    }
    if (query.per_page === undefined && query.is_get_sorted !== true) {
      query.per_page = pageSize.value
    }
    pageSize.value = query.per_page

    return api
      .get(`${_url}?${buildQuery(query)}`)
      .then((res) => {
        if (res.status == 200) {
          totalItem.value = Number(res.headers['x-item-count']) || 0
          items.value = infinityLoad ? [...items.value, ...res.data] : res.data
          if (query.page === 'last') {
            currentPage.value = totalPage.value
          }
        }
      })
      .finally(() => {
        loading.value = false
      })
  }

  const reset = () => {
    currentPage.value = 1
    totalItem.value = 0
    items.value = []
  }

  return {
    currentPage,
    pageSize,
    totalPage,
    totalItem,
    items,
    loading,
    getItems,
    reset
  }
}

export function useNavigationArrow(pageClass: string) {
  const handleKeyDown = (e: any) => {
    // add custom-arrow-key for element you want to use this feature

    const activeElement = document.activeElement as HTMLInputElement
    const condition = !(
      activeElement && activeElement.getAttribute('group') === pageClass
    )
    if (condition) {
      return
    }

    const start = activeElement.selectionStart ?? 0
    const end = activeElement.selectionEnd ?? 0
    const length = activeElement?.value ? activeElement?.value.length : 0
    const isAutocomplete = activeElement.closest('[role="combobox"]')
      ? true
      : false
    const focusableElements =
      '.custom-arrow-key input:not([type="hidden"]):not([type="checkbox"]):not(:disabled)'
    const elements = Array.from(
      document.querySelectorAll(focusableElements)
    ).filter((el) => !(el as HTMLInputElement).hasAttribute('disabled'))
    const index = elements.indexOf(e.target)
    const conditionRight = start === length || (start === 0 && end === length)
    const conditionLeft = start === 0 || (start === 0 && end === length)
    if (
      e.key === 'ArrowRight' &&
      index < elements.length - 1 &&
      conditionRight
    ) {
      const nextInput = elements[index + 1] as HTMLInputElement
      if (nextInput) {
        e.preventDefault()
        e.target.blur()
        setTimeout(() => {
          nextInput.click()
          nextInput.select()
        })
      }
    } else if (e.key === 'ArrowLeft' && index > 0 && conditionLeft) {
      const prevInput = elements[index - 1] as HTMLInputElement
      if (prevInput) {
        e.preventDefault()
        e.target.blur()
        setTimeout(() => {
          prevInput.click()
          prevInput.select()
        })
      }
    } else if (e.key === 'ArrowDown' && !isAutocomplete) {
      let currentRow = e.target.closest('tr')
      if (!currentRow) return
      const currentCellIndex = Array.from(
        currentRow?.querySelectorAll(focusableElements)
      ).indexOf(e.target)
      while (currentRow) {
        currentRow = currentRow.nextElementSibling
        if (!currentRow) break
        const nextInputs = currentRow.querySelectorAll(focusableElements)
        if (nextInputs.length > 0) {
          const nextInput = nextInputs[currentCellIndex]
          if (nextInput) {
            nextInput.click()
            nextInput.select()
            break
          }
        }
      }
      e.preventDefault()
    } else if (e.key === 'ArrowUp' && !isAutocomplete) {
      let currentRow = e.target.closest('tr')
      const currentCellIndex = Array.from(
        currentRow.querySelectorAll(focusableElements)
      ).indexOf(e.target)
      while (currentRow) {
        currentRow = currentRow.previousElementSibling
        if (!currentRow) break
        const prevInputs = currentRow.querySelectorAll(focusableElements)
        if (prevInputs.length > 0) {
          const prevInput = prevInputs[currentCellIndex]
          if (prevInput) {
            prevInput.click()
            prevInput.select()
            break
          }
        }
      }
      e.preventDefault()
    }
  }
  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown)
  })
  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeyDown)
  })
  function focusPreviousInput(index: number, childIndex: number) {
    const firstInput = document.querySelector(
      `.input-${index}-${childIndex - 1} input`
    ) as HTMLInputElement
    if (firstInput) firstInput.click()
  }
  return {
    focusPreviousInput,
    handleKeyDown
  }
}
